'use client';

import { PropsWithChildren, useEffect, useState } from 'react';
import { animated } from '@react-spring/web';
import { cj } from 'src/utils/classNames';
import { NotificationStore } from '../../stores/useNotificationStore';
import { ErrorFilled, SuccessFilled } from '@drift-labs/icons';
import WarningFilled from 'src/components/Icons/WarningFilled';
import ArcLoadingSpinner from '../Icons/LoadingSpinner/ArcLoadingSpinner';
import InfoFilled from '../Icons/InfoFilled';
import { TimeoutProgressIndicator } from '../Utils/TimeoutProgressIndicator';
import ProgressIndicator from './MarketOrders/ProgressIndicator';
import { useSwipeToDismiss } from 'src/components/Toasts/hooks/useSwipeToDismiss';
import {
	TOAST_BACKGROUND_CLASSNAME,
	TOAST_ICON_SIZE,
	TOAST_ROUNDED_CLASSNAME,
	TOAST_SHADOW_CLASSNAME,
	TOAST_WIDTH_CLASSNAME,
} from 'src/components/Toasts/consts';
import XButton from 'src/components/Buttons/XButton';
import { getColorVar } from 'src/utils/theme/colorVars';

type ToastType = Exclude<
	NotificationStore['notifications'][0]['type'],
	'alert' | 'marketOrder' // Exclude market order and alert toasts because they get handled by their own components
>;

const StatusIcon = ({
	type,
	timeoutStartMs,
	timeoutEndMs,
	progressStart,
	progressEnd,
	progressCurrent,
}: {
	type: ToastType;
	timeoutStartMs?: number;
	timeoutEndMs?: number;
	progressStart?: number;
	progressEnd?: number;
	progressCurrent?: number;
}) => {
	let icon: React.ReactNode;

	switch (type) {
		case 'awaiting':
			icon = <ArcLoadingSpinner size={TOAST_ICON_SIZE} />;
			break;
		case 'error':
			icon = (
				<ErrorFilled
					size={TOAST_ICON_SIZE}
					color={getColorVar('negative-red')}
				/>
			);
			break;
		case 'success':
			icon = (
				<SuccessFilled
					size={TOAST_ICON_SIZE}
					color={getColorVar('positive-green')}
				/>
			);
			break;
		case 'progress_indicator':
			icon = (
				<ProgressIndicator
					start={progressStart ?? 0}
					end={progressEnd ?? 0}
					current={progressCurrent ?? 0}
					direction="end-empty"
					type="neutral"
					size={TOAST_ICON_SIZE}
				/>
			);
			break;
		case 'awaiting_wallet_confirmation':
		case 'timeout':
			icon = (
				<TimeoutProgressIndicator
					startTimeMs={timeoutStartMs ?? 0}
					endTimeMs={timeoutEndMs ?? 0}
					size={TOAST_ICON_SIZE}
				/>
			);
			break;
		case 'warning':
			icon = (
				<WarningFilled
					size={TOAST_ICON_SIZE}
					color={getColorVar('star-yellow')}
				/>
			);
			break;
		case 'info':
			icon = (
				<InfoFilled
					fillColor={getColorVar('text-default')}
					size={TOAST_ICON_SIZE}
				/>
			);
			break;
		default: {
			const _unhandledStage: never = type;
			throw new Error(`Unhandled stage: ${_unhandledStage}`);
		}
	}

	return <span style={{ height: TOAST_ICON_SIZE + 'px' }}>{icon}</span>;
};

const Toast = ({
	type,
	onMouseEnter,
	onMouseLeave,
	onClose,
	className,
	timeoutStartMs,
	timeoutEndMs,
	progressStart,
	progressEnd,
	progressCurrent,
	...props
}: PropsWithChildren<{
	type: ToastType;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	onClose?: () => void;
	timeoutStartMs?: number;
	timeoutEndMs?: number;
	progressStart?: number;
	progressEnd?: number;
	progressCurrent?: number;
	className?: string;
}>) => {
	// For some types of toasts we force the component to render once per second so that the progress state of the indicators can be refreshed
	const shouldForceRender =
		type === 'awaiting_wallet_confirmation' ||
		type === 'progress_indicator' ||
		type === 'timeout';

	useForceRender(shouldForceRender);

	const swipeToDismissProps = useSwipeToDismiss({ onDismiss: onClose });

	return (
		<animated.div {...swipeToDismissProps}>
			<div
				className={cj(
					'border border-purple-bg p-lg',
					'pointer-events-auto text-text-default',
					TOAST_BACKGROUND_CLASSNAME,
					TOAST_ROUNDED_CLASSNAME,
					TOAST_SHADOW_CLASSNAME,
					TOAST_WIDTH_CLASSNAME,
					className
				)}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<div className="relative z-1 flex items-start gap-x-sm">
					<StatusIcon
						type={type}
						timeoutStartMs={timeoutStartMs}
						timeoutEndMs={timeoutEndMs}
						progressStart={progressStart}
						progressEnd={progressEnd}
						progressCurrent={progressCurrent}
					/>
					<div className="w-full grow items-center justify-between">
						{props.children}
					</div>
					<XButton size="md" onClick={onClose} />
				</div>
			</div>
		</animated.div>
	);
};

const useForceRender = (enabled: boolean) => {
	const [_, forceRender] = useState(0);
	useEffect(() => {
		if (!enabled) return;
		const interval = setInterval(() => {
			forceRender((prev) => prev + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, [enabled]);
	return _;
};

export default Toast;
